import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import DomainDetail from './DomainDetail';
import {
  RISK_COLOR, RISK_LABEL, BAND_DESCRIPTIONS, BAND_ORDER, SUMMARY_ORDER,
} from './sciRiskBands';
import {
  isMonthKey, currentMonthKey, shiftMonth, monthLabel, monthSpanLabel,
  monthsBetween, fmtReportDate, fmtShortDate,
} from './reportMonths';

// Sci Report — the user's SCI web-activity report, one report per calendar
// month (10-01). The current month is the live report and fills in as the
// desktop app uploads visits; earlier months are the Previous Reports archive.
// Data: GET /bff/activity?month=YYYY-MM (one month, backed by
// `domain_history_monthly`) and GET /bff/activity/months (the index of months
// with activity, with summary counts, so the archive can be listed and the
// month-over-month line drawn without loading every report).
//
// The selected month lives in ?month= so a report can be bookmarked and the
// back button steps through months.
//
// "Verify" is the unranked queue (risk_level NULL): requested but not yet
// scored. It is counted from `pending` alone — the pre-refactor page added
// `transitions` too, which double-counted domains that already appear in a
// risk bucket, so the tiles never summed to the total.

const EMPTY_DISTRIBUTION = { legit: 0, high: 0, elevated: 0, low: 0, total: 0 };
const EMPTY_BUCKETS = { legit: [], high: [], elevated: [], low: [] };

// Thin white rules between cells keep the summary reading as one grid.
// Sized by the grid rather than a min-width, so the strip reflows instead of
// pushing past the content column. The 2px gutters come from the grid's gap,
// which keeps them even in both directions once the cells wrap.
const SummaryCell = ({ label, value, color }) => (
  <div>
    <div className={`${color} text-white text-center py-2 px-1 leading-tight`}>{label}</div>
    <div className="bg-gray-200 text-black text-center py-2 tabular-nums">{value}</div>
  </div>
);

const ArrowButton = ({ onClick, disabled, label, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className="h-9 w-9 flex-shrink-0 rounded-full border border-gray-400 text-lg leading-none text-black transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:border-gray-200 disabled:text-gray-300 disabled:hover:bg-transparent"
  >
    {children}
  </button>
);

// Month-over-month line under the summary strip. Both months come from the
// index so this needs no extra request; a month missing from the index had no
// activity, which counts as zeros.
const signed = (n) => (n > 0 ? `+${n}` : `${n}`);

const MonthDelta = ({ month, index }) => {
  const prevKey = shiftMonth(month, -1);
  const cur = index.find((m) => m.month === month);
  const prev = index.find((m) => m.month === prevKey);
  if (!cur || !prev) return null;

  const parts = ['high', 'elevated', 'low', 'legit']
    .map((band) => ({ band, d: (cur[band] || 0) - (prev[band] || 0) }))
    .filter(({ d }) => d !== 0)
    .map(({ band, d }) => `${signed(d)} ${RISK_LABEL[band]}`);
  const total = cur.total - prev.total;
  const sites = total === 0 ? 'same number of sites' : `${signed(total)} sites`;

  return (
    <p className="mt-2 text-xs text-gray-600">
      vs {monthLabel(prevKey)}: {sites}
      {parts.length > 0 ? ` · ${parts.join(' · ')}` : ''}
    </p>
  );
};

// The archive list inside the header. Every month with activity, newest first;
// the open month is highlighted. Months are also reachable with the arrows,
// which step through empty months too; this list skips straight to the ones
// with something in them.
const PreviousReports = ({ index, month, current, onSelect, loadError }) => {
  const [open, setOpen] = useState(false);
  const rows = index.filter((m) => m.month !== current);

  return (
    <div className="border-b border-gray-400">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between py-3 text-left text-lg text-black hover:underline"
      >
        <span>
          Previous Reports
          {!loadError && <span className="ml-2 text-sm text-gray-500">({rows.length})</span>}
        </span>
        <span aria-hidden="true">{open ? 'v' : '>'}</span>
      </button>

      {open && (
        loadError ? (
          <p className="pb-3 text-sm text-[#B02418]">
            We couldn&rsquo;t load your report history just now. Earlier months are still there; please try again shortly.
          </p>
        ) : rows.length === 0 ? (
          <p className="pb-3 text-sm text-gray-500">
            This is your first report. Each month closes on the 1st and moves here.
          </p>
        ) : (
          <ul className="pb-3 divide-y divide-gray-200">
            {rows.map((m) => {
              const selected = m.month === month;
              const flagged = (m.high || 0) + (m.elevated || 0);
              return (
                <li key={m.month}>
                  <button
                    type="button"
                    onClick={() => onSelect(m.month)}
                    aria-current={selected ? 'true' : undefined}
                    className={`flex w-full items-baseline justify-between gap-4 px-2 py-2 text-left transition-colors hover:bg-gray-50 ${selected ? 'bg-gray-100' : ''}`}
                  >
                    <span className={`text-base ${selected ? 'font-semibold text-black' : 'text-black'}`}>
                      {monthLabel(m.month)}
                    </span>
                    <span className="text-sm text-gray-600 tabular-nums whitespace-nowrap">
                      {m.total} {m.total === 1 ? 'site' : 'sites'}
                      {flagged > 0 && (
                        <>
                          {' · '}
                          <span className="text-[#B02418]">{flagged} flagged</span>
                        </>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )
      )}
    </div>
  );
};

const SciReportPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('month');
  const month = isMonthKey(requested) && requested <= currentMonthKey() ? requested : currentMonthKey();

  // The archive index: months with activity + which month is "now" on the server.
  const [index, setIndex] = useState(null);
  const [indexError, setIndexError] = useState(null);

  // Per-month reports, cached so stepping back and forth doesn't refetch.
  const [reports, setReports] = useState({});
  const [reportError, setReportError] = useState(null);

  const [openBand, setOpenBand] = useState('legit');
  const [expandedKey, setExpandedKey] = useState(null);
  const [detailCache, setDetailCache] = useState({});
  const [historyCache, setHistoryCache] = useState({});

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE_URL}/bff/activity/months`, { credentials: 'include' })
      .then((r) => {
        // 403 = plan without the sci-zone feature; say so rather than showing a raw status.
        if (r.status === 403) throw new Error('locked');
        if (!r.ok) throw new Error(`Report index failed: HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => { if (!cancelled) setIndex(data); })
      .catch((err) => { if (!cancelled) setIndexError(err.message || 'Failed to load report history'); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (reports[month]) return undefined;
    let cancelled = false;
    setReportError(null);
    fetch(`${API_BASE_URL}/bff/activity?month=${month}`, { credentials: 'include' })
      .then((r) => {
        if (r.status === 403) throw new Error('locked');
        if (!r.ok) throw new Error(`Report fetch failed: HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => { if (!cancelled) setReports((prev) => ({ ...prev, [month]: data })); })
      .catch((err) => { if (!cancelled) setReportError(err.message || 'Failed to load report'); });
    return () => { cancelled = true; };
  }, [month, reports]);

  const selectMonth = useCallback((key) => {
    setExpandedKey(null);
    setOpenBand('legit');
    setSearchParams(key === currentMonthKey() ? {} : { month: key });
  }, [setSearchParams]);

  const fetchDetail = useCallback(async (domain) => {
    if (detailCache[domain]) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bff/domains/${encodeURIComponent(domain)}`, { credentials: 'include' });
      if (!r.ok) return;
      const detail = await r.json();
      setDetailCache((prev) => ({ ...prev, [domain]: detail }));
    } catch { /* leave the row in its loading state */ }
  }, [detailCache]);

  const fetchHistory = useCallback(async (domain) => {
    if (historyCache[domain]) return;
    try {
      const r = await fetch(`${API_BASE_URL}/bff/domains/${encodeURIComponent(domain)}/history`, { credentials: 'include' });
      const history = r.ok ? (await r.json()).history || [] : [];
      setHistoryCache((prev) => ({ ...prev, [domain]: history }));
    } catch {
      setHistoryCache((prev) => ({ ...prev, [domain]: [] }));
    }
  }, [historyCache]);

  const report = reports[month];
  const current = index?.current || currentMonthKey();
  const months = useMemo(() => index?.months || [], [index]);
  const isCurrent = month === current;

  // The arrows and the dropdown run from the first month with activity to the
  // current month. A bookmarked month before that (always empty) is still
  // included so the dropdown shows the month that is actually open.
  const earliest = useMemo(() => {
    const keys = months.map((m) => m.month);
    const first = keys.length === 0 ? current : keys.reduce((a, b) => (a < b ? a : b));
    return month < first ? month : first;
  }, [months, current, month]);
  const canGoBack = month > earliest;
  const canGoForward = month < current;
  const range = useMemo(() => monthsBetween(earliest, current), [earliest, current]);

  const distribution = report?.distribution || EMPTY_DISTRIBUTION;
  const buckets = report?.buckets || EMPTY_BUCKETS;
  const pending = report?.pending || [];

  const bandRows = {
    legit: buckets.legit,
    high: buckets.high,
    elevated: buckets.elevated,
    low: buckets.low,
    verify: pending,
  };
  const counts = { ...distribution, verify: pending.length };
  const isEmpty = report && distribution.total === 0;

  const error = indexError || reportError;
  if (error === 'locked' || (error && !index && !report)) {
    return (
      <HubPageLayout>
        <FramedPanel title="Sci Report">
          <div className="px-6 pt-10 pb-10 text-center">
            <p className="text-black text-lg">
              {error === 'locked'
                ? 'The SCI Activity Zone is not included in your current plan.'
                : 'We couldn’t load your Sci Report just now. Please try again shortly.'}
            </p>
          </div>
        </FramedPanel>
      </HubPageLayout>
    );
  }

  if (!index && !report) {
    return (
      <HubPageLayout>
        <p className="py-12 text-center text-gray-400">Loading your Sci Report&hellip;</p>
      </HubPageLayout>
    );
  }

  return (
    <HubPageLayout>
      {/* Report header: hero beside the month navigator and the archive */}
      <FramedPanel title="Sci Report">
        <div className="px-6 pt-8 pb-6">
          <div className="flex flex-col md:flex-row gap-8">
            <img
              src="/images/sci-report/sci-report-hero.jpeg"
              alt=""
              className="md:w-1/2 h-[220px] md:h-[260px] object-cover"
            />
            <div className="md:w-1/2 md:pt-2">
              <p className="text-xs uppercase tracking-wide text-gray-500">Report period</p>

              <div className="mt-1 flex items-center gap-3 border-b border-gray-400 pb-3">
                <ArrowButton
                  onClick={() => selectMonth(shiftMonth(month, -1))}
                  disabled={!canGoBack}
                  label={canGoBack ? `Previous month: ${monthLabel(shiftMonth(month, -1))}` : 'No earlier reports'}
                >
                  &lsaquo;
                </ArrowButton>

                <label className="flex-1 min-w-0">
                  <span className="sr-only">Report month</span>
                  <select
                    value={month}
                    onChange={(e) => selectMonth(e.target.value)}
                    className="w-full cursor-pointer bg-transparent text-xl font-semibold text-black focus:outline-none xl:text-2xl"
                  >
                    {range.slice().reverse().map((k) => (
                      <option key={k} value={k}>
                        {monthLabel(k)}
                      </option>
                    ))}
                  </select>
                </label>

                <ArrowButton
                  onClick={() => selectMonth(shiftMonth(month, 1))}
                  disabled={!canGoForward}
                  label={canGoForward ? `Next month: ${monthLabel(shiftMonth(month, 1))}` : 'This is the current month'}
                >
                  &rsaquo;
                </ArrowButton>
              </div>

              <p className="py-3 text-sm text-black border-b border-gray-400">
                {isCurrent ? (
                  <>
                    <span className="font-semibold text-[#4E9A4E]">In progress</span>
                    {' · '}updates as you browse
                  </>
                ) : (
                  <>
                    <span className="font-semibold">Final</span>
                    {' · '}{monthSpanLabel(month)}
                  </>
                )}
                {report?.period?.last_activity && (
                  <>
                    <br />
                    <span className="text-gray-600">Last activity: {fmtReportDate(report.period.last_activity)}</span>
                  </>
                )}
              </p>

              <PreviousReports
                index={months}
                month={month}
                current={current}
                onSelect={selectMonth}
                loadError={Boolean(indexError)}
              />

              <p className="text-xs text-gray-400 mt-2">
                Covers every domain your desktop app checked in {monthLabel(month)}.
                Reports are kept month by month and do not expire.
              </p>
            </div>
          </div>
        </div>
      </FramedPanel>

      {!report ? (
        <p className="py-12 text-center text-gray-400">
          {reportError
            ? 'We couldn’t load this month’s report. Please try again shortly.'
            : `Loading ${monthLabel(month)}…`}
        </p>
      ) : (
        <>
          {/* SCI Summary strip.
              Six cells beside a label cell needs 680px, which is more than the
              content column has once the viewport drops to about 1200 — the strip
              then ran under the Report rail. Below xl the label moves above the
              numbers and the cells wrap to two rows of three; at xl the original
              single-row strip returns. */}
          <div className="mt-8">
            <div className="mb-0.5 bg-gray-200 px-3 py-2 text-black xl:hidden">SCI Summary</div>

            <div className="flex gap-0.5">
              <div className="hidden w-32 flex-shrink-0 items-center justify-center bg-gray-200 px-2 text-center text-black xl:flex">
                SCI Summary
              </div>

              <div className="grid flex-1 grid-cols-3 gap-0.5 xl:grid-cols-6">
                <SummaryCell label="Total" value={counts.total} color="bg-[#4E9A4E]" />
                {SUMMARY_ORDER.map((band) => (
                  <SummaryCell
                    key={band}
                    label={RISK_LABEL[band]}
                    value={counts[band] ?? 0}
                    color={RISK_COLOR[band]}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Month over month, for closed months only: an in-progress month
              against a full one would read as a drop. */}
          {!isCurrent && <MonthDelta month={month} index={months} />}

          <p className="text-xs text-gray-500 mt-2">
            Ratings are being re-tuned — treat Elevated and Low as directional while the scanner
            re-scores. Bands reflect each site&rsquo;s current rating, so a closed month can change if a site is re-scored.
          </p>

          {/* Detail — one block per band */}
          <h2 className="text-2xl font-bold text-black mt-10 mb-4">Detail</h2>

          {isEmpty ? (
            <div className="bg-gray-50 px-6 py-10 text-center">
              {isCurrent ? (
                <>
                  <p className="text-lg text-black">Nothing checked yet in {monthLabel(month)}.</p>
                  <p className="mt-2 text-sm text-gray-600">
                    Browse with the desktop app running and the sites it checks appear here within a few minutes.
                  </p>
                </>
              ) : (
                <p className="text-lg text-black">No activity was recorded in {monthLabel(month)}.</p>
              )}
            </div>
          ) : BAND_ORDER.map((band) => {
            const rows = bandRows[band] || [];
            const isOpen = openBand === band;
            return (
              <div key={band} className="mb-6">
                <button
                  type="button"
                  onClick={() => setOpenBand(isOpen ? null : band)}
                  className={`w-full text-left ${RISK_COLOR[band]} text-white px-4 py-3 flex items-start justify-between gap-4`}
                >
                  <span>{BAND_DESCRIPTIONS[band]}</span>
                  <span className="flex-shrink-0 whitespace-nowrap">
                    ({rows.length}) {isOpen ? '^' : 'v'}
                  </span>
                </button>

                {isOpen && (
                  rows.length === 0 ? (
                    <p className="text-gray-500 italic px-4 py-4 bg-gray-50">
                      No sites in this band for this report.
                    </p>
                  ) : (
                    rows.map((row) => {
                      const key = `${band}:${row.domain}`;
                      const isExpanded = expandedKey === key;
                      const isVerify = band === 'verify';
                      const visits = Number(row.visit_count) || 0;
                      return (
                        <div key={key}>
                          <div className="flex items-stretch border-b border-white">
                            <div className="w-64 flex-shrink-0 bg-gray-100 px-4 py-3 text-black break-all">
                              {row.domain}
                            </div>
                            <div className="flex flex-1 items-center justify-between gap-4 bg-gray-100 px-4 py-3">
                              {isVerify ? (
                                <span className="text-gray-500 italic">Awaiting ranking</span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setExpandedKey(isExpanded ? null : key);
                                    if (!isExpanded) {
                                      fetchDetail(row.domain);
                                      fetchHistory(row.domain);
                                    }
                                  }}
                                  className="text-black hover:underline"
                                >
                                  {isExpanded ? 'Details v' : 'Details >'}
                                </button>
                              )}
                              {visits > 0 && (
                                <span className="hidden sm:inline text-sm text-gray-500 tabular-nums whitespace-nowrap">
                                  {visits} {visits === 1 ? 'visit' : 'visits'}
                                  {row.last_seen ? ` · last ${fmtShortDate(row.last_seen)}` : ''}
                                </span>
                              )}
                            </div>
                          </div>

                          {isExpanded && (
                            <div className="p-4 bg-gray-50 border-b border-gray-200">
                              <DomainDetail
                                detail={detailCache[row.domain]}
                                history={historyCache[row.domain]}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )
                )}
              </div>
            );
          })}
        </>
      )}
    </HubPageLayout>
  );
};

export default SciReportPage;
